import { Play } from 'lucide-react';

import { VideoItem } from '@features/midia/hooks/useFotosObra';

interface VideoGalleryProps {
  videos: VideoItem[];
  onVideoClick: (index: number) => void;
  modoEscuro?: boolean;
  loading?: boolean;
}

export default function VideoGallery({ 
  videos, 
  onVideoClick, 
  modoEscuro = false,
  loading = false 
}: VideoGalleryProps) {
  
  const getOrigemColor = (origem: string) => {
    switch (origem) {
      case 'diarios': 
        return modoEscuro 
          ? 'bg-blue-900/40 text-blue-400' 
          : 'bg-blue-100 text-blue-800';
      case 'checklist': 
        return modoEscuro 
          ? 'bg-green-900/40 text-green-400' 
          : 'bg-green-100 text-green-800';
      case 'tarefas': 
        return modoEscuro 
          ? 'bg-orange-900/40 text-orange-400' 
          : 'bg-orange-100 text-orange-800';
      default: 
        return modoEscuro 
          ? 'bg-gray-700 text-gray-300' 
          : 'bg-gray-100 text-gray-700';
    }
  };

  const getOrigemLabel = (origem: string) => {
    switch (origem) {
      case 'diarios': return 'D';
      case 'checklist': return 'C';
      case 'tarefas': return 'T';
      default: return '?';
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {Array.from({ length: 12 }).map((_, index) => (
          <div
            key={index}
            className={`aspect-square rounded-lg animate-pulse ${
              modoEscuro ? 'bg-gray-700' : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center py-16 px-4 ${
        modoEscuro ? 'text-gray-400' : 'text-gray-500'
      }`}>
        <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-4 ${
          modoEscuro ? 'bg-gray-700' : 'bg-gray-100'
        }`}>
          <svg 
            className="w-12 h-12" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" 
            />
          </svg>
        </div>
        <h3 className="text-lg font-ageo-bold mb-2">Nenhum vídeo encontrado</h3>
        <p className="text-sm font-ageo-regular text-center max-w-md">
          Os vídeos aparecem automaticamente conforme você adiciona nos diários e tarefas da obra.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
      {videos.map((video, index) => (
        <div
          key={video.id}
          className="group relative cursor-pointer"
          onClick={() => onVideoClick(index)}
        >
          {/* Container do Vídeo */}
          <div className={`aspect-square overflow-hidden rounded-lg border-2 transition-all duration-200 group-hover:shadow-lg group-hover:scale-105 relative ${
            modoEscuro 
              ? 'border-gray-600 group-hover:border-gray-500' 
              : 'border-gray-200 group-hover:border-gray-300'
          }`}>
            {/* Thumbnail do vídeo */}
            <video
              src={video.url}
              className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-110"
              preload="metadata"
              muted
            />
            
            {/* Ícone de Play centralizado */}
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-all duration-200">
              <div className={`p-3 rounded-full transition-all duration-200 group-hover:scale-110 ${
                modoEscuro 
                  ? 'bg-white/20 backdrop-blur-sm' 
                  : 'bg-white/90 backdrop-blur-sm'
              }`}>
                <Play className={`w-6 h-6 ${
                  modoEscuro ? 'text-white' : 'text-gray-800'
                }`} />
              </div>
            </div>
            
            {/* Badge de origem */}
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-ageo-bold ${getOrigemColor(video.origem)}`}>
                {getOrigemLabel(video.origem)}
              </span>
            </div>
          </div>

          {/* Nome do vídeo (aparece no hover) */}
          <div className={`absolute bottom-0 left-0 right-0 p-2 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
            modoEscuro 
              ? 'bg-gray-900/90 backdrop-blur-sm' 
              : 'bg-white/90 backdrop-blur-sm'
          }`}>
            <p className={`text-xs font-ageo-regular truncate ${
              modoEscuro ? 'text-gray-100' : 'text-gray-900'
            }`}>
              {video.nome}
            </p>
            <p className={`text-xs font-ageo-regular ${
              modoEscuro ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {new Date(video.criado_em).toLocaleDateString('pt-BR')}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}